import { useState } from 'react'
import { lookupVehicleByVIN, lookupVehicleByRego, VehicleData, AutograbError, AustralianState } from '../lib/autograb'
import { User } from '../types'
import './VehicleLookup.css'

const AU_STATES: AustralianState[] = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT']

interface VehicleLookupProps {
  onVehicleFound: (vehicleData: VehicleData) => void
  user: User
}

export default function VehicleLookup({ onVehicleFound, user }: VehicleLookupProps) {
  const [lookupType, setLookupType] = useState<'vin' | 'rego'>('vin')
  const [searchValue, setSearchValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [vehicleData, setVehicleData] = useState<VehicleData | null>(null)
  const [showManualOverride, setShowManualOverride] = useState(false)
  const [state, setState] = useState<AustralianState>('VIC')

  const isAdmin = user.role === 'admin'

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setVehicleData(null)
    setLoading(true)

    try {
      if (!searchValue.trim()) {
        throw new Error(`Please enter a ${lookupType === 'vin' ? 'VIN' : 'registration'} number`)
      }

      let result: VehicleData | null = null

      if (lookupType === 'vin') {
        result = await lookupVehicleByVIN(searchValue.toUpperCase())
      } else {
        result = await lookupVehicleByRego(searchValue.toUpperCase(), state)
      }

      if (!result) {
        // Vehicle not found
        if (isAdmin) {
          // Admin can override
          setShowManualOverride(true)
          setError(
            `${lookupType === 'vin' ? 'VIN' : 'Registration'} not found in Autograb. You can continue with manual entry.`
          )
        } else {
          // Regular user cannot override
          setError(
            `${lookupType === 'vin' ? 'VIN' : 'Registration'} not found. Please verify and try again.`
          )
        }
        return
      }

      setVehicleData(result)
    } catch (err) {
      const error = err as AutograbError | Error
      setError(error instanceof Error ? error.message : 'Lookup failed. Please try again.')
      setShowManualOverride(isAdmin)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = () => {
    if (vehicleData) {
      onVehicleFound(vehicleData)
    }
  }

  const handleManualOverride = () => {
    // Create minimal vehicle data for manual entry
    const manualData: VehicleData = {
      vin: lookupType === 'vin' ? searchValue.toUpperCase() : '',
      make: '',
      model: '',
      year: new Date().getFullYear(),
      registrationNumber: lookupType === 'rego' ? searchValue.toUpperCase() : '',
      state: lookupType === 'rego' ? state : undefined
    }
    onVehicleFound(manualData)
  }

  // Show vehicle results if found
  if (vehicleData) {
    return (
      <div className="vehicle-lookup">
        <div className="vehicle-results">
          <div className="results-header">
            <h3>✅ Vehicle Found</h3>
          </div>

          <div className="vehicle-details">
            <div className="detail-row">
              <span className="label">Make</span>
              <span className="value">{vehicleData.make}</span>
            </div>
            <div className="detail-row">
              <span className="label">Model</span>
              <span className="value">{vehicleData.model}</span>
            </div>
            <div className="detail-row">
              <span className="label">Year</span>
              <span className="value">{vehicleData.year}</span>
            </div>
            {vehicleData.odometer && (
              <div className="detail-row">
                <span className="label">Odometer</span>
                <span className="value">{vehicleData.odometer.toLocaleString()} km</span>
              </div>
            )}
            {vehicleData.color && (
              <div className="detail-row">
                <span className="label">Color</span>
                <span className="value">{vehicleData.color}</span>
              </div>
            )}
            {vehicleData.marketValue && (
              <div className="detail-row highlight">
                <span className="label">Market Value</span>
                <span className="value">${vehicleData.marketValue.toLocaleString()}</span>
              </div>
            )}
            {vehicleData.vin && (
              <div className="detail-row">
                <span className="label">VIN</span>
                <span className="value mono">{vehicleData.vin}</span>
              </div>
            )}
          </div>

          <div className="results-actions">
            <button
              type="button"
              className="primary-button"
              onClick={handleConfirm}
            >
              Use This Vehicle
            </button>
            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                setVehicleData(null)
                setSearchValue('')
              }}
            >
              Search Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Show search form
  return (
    <div className="vehicle-lookup">
      <form onSubmit={handleLookup}>
        <div className="lookup-tabs">
          <button
            type="button"
            className={`tab ${lookupType === 'vin' ? 'active' : ''}`}
            onClick={() => {
              setLookupType('vin')
              setSearchValue('')
              setError('')
            }}
          >
            Look up by VIN
          </button>
          <button
            type="button"
            className={`tab ${lookupType === 'rego' ? 'active' : ''}`}
            onClick={() => {
              setLookupType('rego')
              setSearchValue('')
              setError('')
            }}
          >
            Look up by Rego
          </button>
        </div>

        <div className="lookup-form">
          <div className="form-group">
            <label htmlFor="search-value">
              {lookupType === 'vin' ? 'VIN (17 characters)' : 'Registration Number'}
            </label>
            <input
              id="search-value"
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value.toUpperCase())}
              placeholder={lookupType === 'vin' ? 'e.g., ABC123DEF456GHI78' : 'e.g., ABC123'}
              disabled={loading}
            />
          </div>

          {lookupType === 'rego' && (
            <div className="form-group">
              <label htmlFor="rego-state">State</label>
              <select
                id="rego-state"
                value={state}
                onChange={(e) => setState(e.target.value as AustralianState)}
                disabled={loading}
              >
                {AU_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <div className={`error-message ${isAdmin && showManualOverride ? 'warning' : ''}`}>
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              disabled={loading || !searchValue.trim()}
              className="primary-button"
            >
              {loading ? 'Searching...' : 'Look Up Vehicle'}
            </button>

            {isAdmin && showManualOverride && error && (
              <button
                type="button"
                className="secondary-button"
                onClick={handleManualOverride}
              >
                Continue with Manual Entry
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div className="loading-spinner">
            Searching Autograb database...
          </div>
        )}
      </form>
    </div>
  )
}
